import { CNS_API_BASE } from "@/config";
import {
  CNSCreateExperiment,
  CNSExperiment,
  CNSExperimentPage,
  CNSUser,
  CNSEntityChangeHistory,
  CNSEntityChangeHistoryPage,
  JsonPatchOperation,
} from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";

const EXPERIMENTS_BASE = `${CNS_API_BASE}/projects`;
const EXPERIMENT_BRIEF_FALLBACK_FILENAME = "swiftcns-experiment-brief.pdf";

export interface OpenExperimentConversationResult {
  conversationPublicId: string;
  created: boolean;
}

export interface ExperimentBriefPdf {
  blob: Blob;
  filename: string;
}

function decodeExtendedFilename(value: string): string {
  const encodedValue = value.match(/^[^']*'[^']*'(.*)$/)?.[1] ?? value;
  try {
    return decodeURIComponent(encodedValue);
  } catch {
    return encodedValue;
  }
}

function sanitizePdfFilename(value: string | undefined): string {
  if (!value) return EXPERIMENT_BRIEF_FALLBACK_FILENAME;

  const leafName = value.replaceAll("\\", "/").split("/").at(-1) ?? "";
  const withoutControlCharacters = Array.from(leafName)
    .filter((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint >= 32 && codePoint !== 127;
    })
    .join("");
  const safeName = withoutControlCharacters
    .replace(/[<>:"|?*]/g, "-")
    .replace(/^\.+/, "")
    .replace(/[.\s]+$/g, "")
    .trim();

  if (!safeName) return EXPERIMENT_BRIEF_FALLBACK_FILENAME;

  const withExtension = /\.pdf$/i.test(safeName) ? safeName : `${safeName}.pdf`;
  if (withExtension.length <= 180) return withExtension;

  const truncatedBase = withExtension
    .replace(/\.pdf$/i, "")
    .slice(0, 176)
    .replace(/[.\s]+$/g, "");
  return truncatedBase
    ? `${truncatedBase}.pdf`
    : EXPERIMENT_BRIEF_FALLBACK_FILENAME;
}

/**
 * Resolves a safe local filename from an RFC 6266 Content-Disposition value.
 * RFC 5987 filename* takes precedence; missing or unusable values get a stable fallback.
 */
export function resolveExperimentBriefFilename(
  contentDisposition: string | null,
): string {
  if (!contentDisposition) return EXPERIMENT_BRIEF_FALLBACK_FILENAME;

  const extendedMatch = contentDisposition.match(
    /(?:^|;)\s*filename\*\s*=\s*(?:"([^"]*)"|([^;]*))/i,
  );
  const extendedValue = (extendedMatch?.[1] ?? extendedMatch?.[2])?.trim();
  if (extendedValue) {
    return sanitizePdfFilename(decodeExtendedFilename(extendedValue));
  }

  const standardMatch = contentDisposition.match(
    /(?:^|;)\s*filename\s*=\s*(?:"((?:\\.|[^"])*)"|([^;]*))/i,
  );
  const standardValue = (standardMatch?.[1] ?? standardMatch?.[2])
    ?.trim()
    .replace(/\\(["\\])/g, "$1");
  return sanitizePdfFilename(standardValue);
}

/** Fetches the persisted, project-authorized experiment brief as a PDF blob. */
export async function fetchExperimentBriefPdf(
  projectId: string,
  experimentId: string,
): Promise<ExperimentBriefPdf> {
  const res = await fetch(
    `${EXPERIMENTS_BASE}/${encodeURIComponent(projectId)}/experiments/${encodeURIComponent(experimentId)}/brief.pdf`,
    {
      credentials: "include",
      headers: { Accept: "application/pdf" },
    },
  );
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to export experiment brief");
  }

  return {
    blob: await res.blob(),
    filename: resolveExperimentBriefFilename(
      res.headers.get("content-disposition"),
    ),
  };
}

/**
 * Reuses or creates the experiment's authoritative workbench conversation.
 * The backend owns authorization, lineage, and concurrent idempotency.
 */
export async function openExperimentWorkbenchConversation(
  projectId: string,
  experimentId: string,
): Promise<OpenExperimentConversationResult> {
  const res = await fetch(
    `${EXPERIMENTS_BASE}/${projectId}/experiments/${experimentId}/workbench-conversation`,
    {
      method: "PUT",
      credentials: "include",
    },
  );
  if (!res.ok) {
    await throwOnProblemResponse(res, "Failed to open experiment in Workbench");
  }
  return res.json();
}

// Create a new experiment (POST /experiments)
export async function createExperiment(
  body: CNSCreateExperiment
): Promise<CNSExperiment> {
  const res = await fetch(`${CNS_API_BASE}/experiments`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to create experiment");
  return res.json();
}

// Fetch experiments for a project
export async function fetchExperiments(projectId: string): Promise<CNSExperiment[]> {
  const res = await fetch(`${CNS_API_BASE}/projects/${projectId}/experiments`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch experiments");
  const data: CNSExperimentPage = await res.json();
  return data.content || [];
}

/**
 * Fetches users eligible to be assigned to experiments in the given project
 * (i.e. users with editor access to the project).
 */
export async function fetchProjectUsersAssignable(projectId: string): Promise<CNSUser[]> {
  const res = await fetch(`${CNS_API_BASE}/projects/${projectId}/users/assignable`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch assignable users");
  return res.json();
}

// Fetch a single experiment by ID
export async function fetchExperiment(
  projectId: string,
  experimentId: string
): Promise<CNSExperiment> {
  const res = await fetch(
    `${EXPERIMENTS_BASE}/${projectId}/experiments/${experimentId}`,
    { credentials: "include" }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch experiment");
  return res.json();
}

// Update experiment status using PATCH
export async function updateExperimentStatus(
  projectId: string,
  experimentId: string,
  status: CNSExperiment["status"]
): Promise<CNSExperiment> {
  const patchBody: JsonPatchOperation[] = [
    { op: "replace", path: "/status", value: status }
  ];
  const res = await fetch(
    `${EXPERIMENTS_BASE}/${projectId}/experiments/${experimentId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(patchBody),
    }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to update experiment status");
  return res.json();
}

/**
 * Sets (or clears with `null`) the assignee of an experiment.
 *
 * Note: the JSON Patch path internally uses the entity field name
 * (`/assigneeUserPublicId`). The rest of the frontend only references
 * `assigneeUserId` to keep the public API contract clean.
 */
export async function updateExperimentAssignee(
  projectId: string,
  experimentId: string,
  assigneeUserId: string | null,
): Promise<CNSExperiment> {
  return updateExperiment(projectId, experimentId, [
    { op: "replace", path: "/assigneeUserPublicId", value: assigneeUserId },
  ]);
}

/**
 * Sets (or clears with `null`) the due date/time of an experiment.
 *
 * The JSON Patch path uses the entity field name (`/dueAt`). The value should be
 * a UTC ISO 8601 timestamp string (e.g. `2026-04-23T18:30:00Z`) or `null` to clear.
 */
export async function updateExperimentDueAt(
  projectId: string,
  experimentId: string,
  dueAt: string | null,
): Promise<CNSExperiment> {
  return updateExperiment(projectId, experimentId, [
    { op: "replace", path: "/dueAt", value: dueAt },
  ]);
}

/**
 * Partially update an experiment using JSON Patch.
 *
 * @param conversationId When set, backend evaluates narrative field authority against the conversation path.
 */
export async function updateExperiment(
  projectId: string,
  experimentId: string,
  patchOperations: JsonPatchOperation[],
  conversationId?: string,
): Promise<CNSExperiment> {
  const search = new URLSearchParams();
  if (conversationId) search.set("conversationId", conversationId);
  const qs = search.toString();
  const res = await fetch(
    `${EXPERIMENTS_BASE}/${projectId}/experiments/${experimentId}${qs ? `?${qs}` : ""}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(patchOperations),
    }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to update experiment");
  return res.json();
}

// Fetch change history for an experiment
export async function fetchExperimentChangeHistory(
  projectId: string,
  experimentId: string
): Promise<CNSEntityChangeHistory[]> {
  const res = await fetch(
    `${EXPERIMENTS_BASE}/${projectId}/experiments/${experimentId}/change-history`,
    { credentials: "include" }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch experiment change history");
  const data: CNSEntityChangeHistoryPage = await res.json();
  return data.content || [];
}

// Delete an experiment (soft delete)
export async function deleteExperiment(
  projectId: string,
  experimentId: string
): Promise<void> {
  const res = await fetch(
    `${EXPERIMENTS_BASE}/${projectId}/experiments/${experimentId}`,
    { method: "DELETE", credentials: "include" }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to delete experiment");
}
